import { Loader2 } from "lucide-react";
import React, { forwardRef } from "react";
import { cn } from "../../utils/cn";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "ghost"
    | "destructive"
    | "success";
  size?: "sm" | "md" | "lg" | "xl";
  isLoading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  fullWidth?: boolean;
  gradient?: boolean;
  glow?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      isLoading = false,
      loadingText,
      icon,
      iconPosition = "left",
      fullWidth = false,
      gradient = false,
      glow = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const baseClasses = [
      "relative inline-flex items-center justify-center",
      "font-medium transition-all duration-200 ease-in-out",
      "focus:outline-hidden focus:ring-2 focus:ring-offset-2",
      "disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none",
      "overflow-hidden group",
      "active:scale-[0.97]",
      "select-none",
      fullWidth && "w-full",
      "[&::before]:content-none [&::after]:content-none",
    ]
      .filter(Boolean)
      .join(" ");

    const sizeClasses = {
      sm: "px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-md gap-1.5 sm:gap-2 min-h-[32px] sm:min-h-[36px] shadow-xs",
      md: "px-3.5 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-2.5 text-sm sm:text-sm md:text-base rounded-lg gap-2 sm:gap-2.5 min-h-[36px] sm:min-h-[40px] md:min-h-[42px] shadow-xs",
      lg: "px-4 py-2.5 sm:px-5 sm:py-3 md:px-6 md:py-3 lg:px-7 lg:py-3.5 text-base sm:text-base md:text-lg rounded-lg gap-2 sm:gap-2.5 md:gap-3 min-h-[40px] sm:min-h-[44px] md:min-h-[48px] shadow-md",
      xl: "px-5 py-3 sm:px-6 sm:py-3.5 md:px-8 md:py-4 lg:px-10 lg:py-5 text-lg sm:text-lg md:text-xl rounded-xl gap-2.5 sm:gap-3 md:gap-4 min-h-[44px] sm:min-h-[48px] md:min-h-[56px] shadow-md",
    };

    const variantClasses = {
      primary: gradient
        ? "bg-linear-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 hover:shadow-lg hover:shadow-purple-500/30 focus:ring-purple-500 focus:ring-offset-2 disabled:from-purple-400 disabled:to-blue-400"
        : "bg-purple-600 text-white hover:bg-purple-700 hover:shadow-lg hover:shadow-purple-500/30 active:bg-purple-800 active:shadow-md focus:ring-purple-500 focus:ring-offset-2 disabled:bg-purple-400",
      secondary:
        "bg-gray-600 text-white hover:bg-gray-700 hover:shadow-lg hover:shadow-gray-500/20 active:bg-gray-800 active:shadow-md focus:ring-gray-500 focus:ring-offset-2 disabled:bg-gray-400",
      outline:
        "border-2 border-purple-600 text-purple-600 bg-transparent hover:bg-purple-600 hover:text-white hover:shadow-lg hover:shadow-purple-500/20 active:bg-purple-700 active:border-purple-700 active:shadow-md focus:ring-purple-500 focus:ring-offset-2 disabled:border-purple-300 disabled:text-purple-300 disabled:hover:bg-transparent",
      ghost:
        "text-gray-700 bg-transparent hover:bg-purple-50 hover:text-purple-700 active:bg-purple-100 active:text-purple-800 focus:ring-purple-500 focus:ring-offset-2 disabled:text-gray-400 disabled:hover:bg-transparent",
      destructive:
        "bg-red-600 text-white hover:bg-red-700 hover:shadow-lg hover:shadow-red-500/30 active:bg-red-800 active:shadow-md focus:ring-red-500 focus:ring-offset-2 disabled:bg-red-400",
      success:
        "bg-green-600 text-white hover:bg-green-700 hover:shadow-lg hover:shadow-green-500/30 active:bg-green-800 active:shadow-md focus:ring-green-500 focus:ring-offset-2 disabled:bg-green-400",
    };

    const glowClasses = glow
      ? "shadow-lg hover:shadow-xl hover:shadow-purple-500/40"
      : "";

    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        className={cn(
          baseClasses,
          sizeClasses[size],
          variantClasses[variant],
          glowClasses,
          className,
        )}
        onContextMenu={(e) => {
          if (props.onClick && !isDisabled) {
            e.preventDefault();
            props.onClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
          }
        }}
        disabled={isDisabled}
        {...props}
      >
        {/* Shimmer Effect - apenas quando não está desabilitado */}
        {!isDisabled && (
          <span className="absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />
        )}

        {/* Overlay escuro quando desabilitado */}
        {isDisabled && (
          <span
            className="absolute inset-0 bg-black/10 pointer-events-none"
            style={{ borderRadius: "inherit" }}
          />
        )}

        {/* Loading Spinner */}
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            {loadingText && (
              <span className="relative ml-2">{loadingText}</span>
            )}
            {!loadingText && children && (
              <span className="relative ml-2">{children}</span>
            )}
          </>
        ) : (
          <>
            {/* Left Icon */}
            {icon && iconPosition === "left" && (
              <span className="shrink-0">{icon}</span>
            )}

            {/* Button Text */}
            {children && <span className="relative">{children}</span>}

            {/* Right Icon */}
            {icon && iconPosition === "right" && (
              <span className="shrink-0">{icon}</span>
            )}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
